import { AgGridReact } from "ag-grid-react";
import React, { useState, useEffect, useMemo } from "react";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-quartz.css";
import "ag-grid-community/styles/ag-theme-alpine.css";
import "ag-grid-community/styles/ag-theme-balham.css";
import "ag-grid-community/styles/ag-theme-material.css";
import "../../global-styles/AGGrid.scss";
import "../../global-styles/AGGrid-light-theme.scss";

import {
  ClientSideRowModelModule,
  ColumnsToolPanelModule,
  FiltersToolPanelModule,
  LicenseManager,
  MenuModule,
  PaginationModule,
  RowGroupingModule,
  SetFilterModule,
  StatusBarModule,
  ExcelExportModule,
  CsvExportModule,
  DateFilterModule,
  MultiFilterModule,
  TextFilterModule,
  NumberFilterModule,
  themeMaterial,
  RowSelectionModule,
  NumberEditorModule,
  TextEditorModule,
  ValidationModule,
  ColumnApiModule,
} from "ag-grid-enterprise";
import { useSelector } from "react-redux";

// Set AG Grid Enterprise License Key
LicenseManager.setLicenseKey(process.env.REACT_APP_AG_GRID_ENTERPRISE_LICENSE);
const EnterpriseGridCommon = ({
  rows,
  columnDefs,
  isLoading,
  onRowClicked,
  exportFileName,
  handleFilterChanged,
  processCellCallback,
  heightValue,
  Data,
  showRowSelection,
  onRowSelected,
  onCellValueChanged,
  onColumnsVisibleChanged,
  disableStatusBar,
  comingFrom,
  setSelectedParams,
}) => {
  const [theme, setTheme] = useState("ag-theme-material-dark"); // Default theme

  const reducerList = useSelector((state) => state);

  const themeState = reducerList?.switchThemeReducer?.switchTheme;

  useEffect(() => {
    if (themeState) {
      setTheme("ag-theme-material");
    } else {
      setTheme("ag-theme-material-dark");
    }
  }, [themeState]);

  const filteredRows = useMemo(() => {
    return rows?.filter((row) => row && Object.keys(row).length > 0) || [];
  }, [rows]);

  const [gridApi, setGridApi] = useState(null);

  const onGridReady = (params) => {
    setGridApi(params.api);
  };

  const [rowData, setRowData] = useState(filteredRows);

  useEffect(() => {
    setRowData(filteredRows);
  }, [filteredRows]);

  const sideBar = useMemo(() => {
    return {
      toolPanels: [
        {
          id: "columns",
          labelDefault: "Columns",
          labelKey: "columns",
          iconKey: "columns",
          toolPanel: "agColumnsToolPanel",
          toolPanelParams: {
            // Hide the Values section
            suppressPivotMode: true,
            suppressValues: true,
            suppressRowGroups: true,
            suppressPivots: true,
          },
        },
        "filters",
      ],
    };
  }, []);

  const defaultCsvExportParams = useMemo(() => {
    return {
      fileName: `${exportFileName}.csv`, // Default file name
      allColumns: true, // Include all columns by default
      onlySelected: false, // Include all rows, not just selected ones
      ...(processCellCallback
        ? {
            processCellCallback: processCellCallback,
          }
        : {}),
    };
  }, []);

  const defaultExcelExportParams = useMemo(() => {
    return {
      fileName: `${exportFileName}.xlsx`, // Default file name
      allColumns: true, // Include all columns by default
      onlySelected: false, // Include all rows, not just selected ones
      sheetName: "Sheet1", // Default sheet name
      ...(processCellCallback
        ? {
            processCellCallback: processCellCallback,
          }
        : {}),
    };
  }, []);

  const autoGroupColumnDef = useMemo(() => {
    return {
      minWidth: 50,
      pinned: "left",
      suppressAutoSize: true,
      cellClassRules: {
        "total-group-row": (params) => params.node.footer, // Apply class for total rows
      },
      cellRendererParams: {
        suppressCount: true,
      },
    };
  }, []);

  const defaultColDef = useMemo(() => {
    return {
      // flex: 1,
      ...(Data ? { flex: 1 } : {}),
      width: 180,
      minWidth: 100,
      maxWidth: 500,
      resizable: true,
      enableValue: true,
      enableRowGroup: false,
      floatingFilter: true,
      enablePivot: true,
      filter: true,
      wrapHeaderText: true,
      autoHeaderHeight: true,
      suppressAutoSize: true,
      filterParams: {
        comparator: (a, b) => a.localeCompare(b),
      },
      cellClassRules: {
        "pivot-total-value": (params) => params.node.footer,
      },
    };
  }, [Data]);

  const gridOptions = useMemo(
    () => ({
      domLayout: "autoHeight",
      enableRangeSelection: true,
      suppressExcelExport: false,
    }),
    []
  );

  const statusBar = {
    statusPanels: [
      {
        statusPanel: "agTotalRowCountComponent",
        align: "left",
      },
      {
        statusPanel: "agFilteredRowCountComponent",
        align: "left",
      },
    ],
  };

  const rowSelection = useMemo(() => {
    return showRowSelection
      ? {
          mode: "multiRow",
        }
      : {};
  }, [showRowSelection]);

  const [isRendered, setIsRendered] = useState(false);

  useEffect(() => {
    if (gridApi) {
      setIsRendered(true);
      //for the check box , just to make sure the onSelectionChanged renders after the render is completed
      //what happens other wise is that it resets the value
    }
  }, [gridApi]);

  const onSelectionChanged = () => {
    if (gridApi && isRendered) {
      const selectedRows = gridApi.getSelectedRows();
      if (comingFrom === "status-configuration") {
        // Create a new object with all parameters set to false
        const updatedParams = {
          heat: false,
          volts: false,
          current: false,
          wire_speed: false,
          true_energy: false,
          travel_speed: false,
          oscillation_width: false,
        };

        // Map the selected parameters to their corresponding keys
        selectedRows?.forEach((ite) => {
          const param = ite.parameter.split("(")[0].trim().toLowerCase();
          switch (param) {
            case "current":
              updatedParams.current = true;
              break;
            case "volts":
              updatedParams.volts = true;
              break;
            case "wire speed":
              updatedParams.wire_speed = true;
              break;
            case "travel speed":
              updatedParams.travel_speed = true;
              break;
            case "true energy":
              updatedParams.true_energy = true;
              break;
            case "heat":
              updatedParams.heat = true;
              break;
            case "oscillation width":
              updatedParams.oscillation_width = true;
              break;
          }
        });
        setSelectedParams(updatedParams);
      } else {
        const rows = selectedRows?.map((ite) => ite.event);
        onRowSelected(rows);
      }
    }
  };

  const calcHeight = heightValue || -250;
  const gridStyle = useMemo(() => ({ height: "99%", width: "100%" }), []);
  return (
    <div
      style={{
        height: `calc(${calcHeight}px + 100vh)`,
        overflow: "auto",
        position: "relative",
      }}
    >
      <div className={`ag-grid ${theme} custom-grid-theme }`} style={gridStyle}>
        <AgGridReact
          theme={{ id: "themeMaterial", theme: themeMaterial }}
          modules={[
            ColumnsToolPanelModule,
            FiltersToolPanelModule,
            MenuModule,
            SetFilterModule,
            !disableStatusBar ? StatusBarModule : {},
            RowGroupingModule,
            PaginationModule,
            ExcelExportModule,
            CsvExportModule,
            DateFilterModule,
            MultiFilterModule,
            TextFilterModule,
            SetFilterModule,
            NumberFilterModule,
            RowSelectionModule,
            ClientSideRowModelModule,
            NumberEditorModule,
            TextEditorModule,
            ValidationModule,
            ColumnApiModule,
          ]}
          singleClickEdit={true}
          rowSelection={rowSelection}
          onGridReady={onGridReady}
          onFilterChanged={() => {
            if (handleFilterChanged) {
              if (gridApi) {
                const filterModel = gridApi.getFilterModel();
                handleFilterChanged(filterModel);
              }
            }
          }}
          gridOptions={gridOptions}
          defaultColDef={defaultColDef}
          autoGroupColumnDef={autoGroupColumnDef}
          columnDefs={
            comingFrom == "status-configuration"
              ? columnDefs
              : columnDefs?.map((ite) => {
                  delete ite.title;
                  return {
                    ...ite,
                  };
                })
          }
          rowData={rowData}
          defaultExcelExportParams={defaultExcelExportParams}
          defaultCsvExportParams={defaultCsvExportParams}
          pivotPanelShow={"always"}
          rowDragManaged={true}
          sideBar={sideBar}
          onSelectionChanged={onSelectionChanged}
          animateRows={true}
          enableFilter={true}
          groupDefaultExpanded={2}
          pivotDefaultExpanded={2}
          groupTotalRow={null}
          grandTotalRow={null}
          groupDisplayType={"multipleColumns"}
          showOpenedGroup={true}
          suppressGroupRowsSticky={true}
          groupHideOpenParents={true}
          rowHeight={42}
          headerHeight={50}
          enableExcelExport={true}
          suppressMenuHide={true}
          enableStrictPivotColumnOrder={true}
          removePivotHeaderRowWhenSingleValueColumn={true}
          loadingOverlayComponentParams={{
            loadingMessage: "Loading all rows...",
          }}
          onColumnVisible={(params) => {
            onColumnsVisibleChanged(params);
          }}
          onCellValueChanged={onCellValueChanged}
          statusBar={statusBar}
          onRowClicked={onRowClicked}
          rowModelType="clientSide"
          loading={isLoading}
          domLayout="normal"
        />
      </div>
    </div>
  );
};

export default EnterpriseGridCommon;
